// Document.tsx
import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import PdfPreviewLazy from './ui/PDFPreviewLazy';
import Modal from './ui/Modal';
import { IconMaximize, IconX, IconChevronDown } from '@tabler/icons-react';
import { FullPdfViewerLazy } from './ui/FullPDFViewerLazy';
import DocumentationRetrievedFeedback from './DocumentationRetrievedFeedback';
import type { DocFeedbackSummary } from './Documents';
import BACKEND_URL from '../config';

interface DocumentProps {
  document: {
    id: number;
    document_id: string;
    name: string | null;
    raw_document_presigned_url: string | null;
    file_type?: string | null;
    text_document_presigned_url?: string | null;
  };
  document_name: string;
  similarity_score: number;
  metadata: {
    text_characteristics: string[];
    non_text_characteristics: string[];
  };
  documentation_retrieved_id: number;
  feedback?: DocFeedbackSummary;
  onFeedbackSubmitted?: (docId: number, rating: number) => void;
}

const DOC_TYPES = ['ARO', 'ORO', 'HBOEK'] as const;
type DocType = typeof DOC_TYPES[number];

const DOC_TYPE_STYLES: Record<DocType, string> = {
  ARO: 'bg-blue-100 text-blue-800 border border-blue-200',
  ORO: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
  HBOEK: 'bg-amber-100 text-amber-800 border border-amber-200',
};

const Document: React.FC<DocumentProps> = ({
  document,
  document_name,
  similarity_score,
  metadata,
  documentation_retrieved_id,
  feedback,
  onFeedbackSubmitted,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const hasUrl = Boolean(document.raw_document_presigned_url);

  const fileType = document.file_type?.toLowerCase();
  const isTextDocument =
    fileType === 'text' ||
    fileType === 'markdown' ||
    (fileType == null && document.name != null && !document.name.toLowerCase().endsWith('.pdf'));

  const [text, setText] = useState<string | null>(null);
  const [textError, setTextError] = useState<string | null>(null);
  const [textLoading, setTextLoading] = useState(false);

  useEffect(() => {
    if (!isTextDocument) return;

    let cancelled = false;
    setTextLoading(true);
    setTextError(null);
    setText(null);

    fetch(`${BACKEND_URL}/vnog/get_document_text/${document.id}/`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'X-CSRFToken': Cookies.get('csrftoken') || '',
      },
    })
      .then(async (response) => {
        if (cancelled) return;
        if (!response.ok) {
          setTextError('Geen tekst beschikbaar voor dit document.');
          return;
        }
        const data = await response.json();
        if (cancelled) return;
        setText(typeof data.text === 'string' ? data.text : '');
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setTextError(e instanceof Error ? e.message : 'Onbekende fout bij ophalen tekst.');
      })
      .finally(() => {
        if (!cancelled) setTextLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isTextDocument, document.id]);

  const hasMatchMeta =
    metadata.text_characteristics.length > 0 || metadata.non_text_characteristics.length > 0;
  const docType: DocType = DOC_TYPES[document.id % DOC_TYPES.length];
  const canOpenFull = isTextDocument ? text !== null : hasUrl;

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const renderPreview = () => {
    if (expanded) {
      return (
        <div className="w-full h-full bg-gray-100 rounded border border-gray-200 p-2 overflow-y-auto flex flex-col gap-1">
          <p className="text-xs text-gray-700">
            <span className="font-medium">Similarity:</span> {(similarity_score * 100).toFixed(1)}%
          </p>
          {hasMatchMeta && (
            <p className="text-xs text-gray-700">
              <span className="font-medium">Match LMC:</span>{' '}
              {metadata.text_characteristics.join(', ')} {metadata.non_text_characteristics.join(', ')}
            </p>
          )}
        </div>
      );
    }

    if (isTextDocument) {
      return (
        <div className="w-full h-full bg-gray-50 rounded border border-gray-200 p-2 overflow-y-auto">
          {textLoading && <p className="text-xs text-gray-500">Tekst laden…</p>}
          {!textLoading && textError && <p className="text-xs text-gray-500">{textError}</p>}
          {!textLoading && !textError && text !== null && (
            <pre className="whitespace-pre-wrap text-xs font-mono text-gray-800">{text}</pre>
          )}
        </div>
      );
    }

    if (hasUrl) {
      return (
        <PdfPreviewLazy
          pdfUrl={document.raw_document_presigned_url!}
          width={208}
          height={180}
        />
      );
    }

    return <p className="text-xs text-gray-600">No preview available. Document URL is missing.</p>;
  };

  return (
    <div className="w-56 flex-none box-border relative border border-gray-200 rounded-lg p-2 bg-white flex flex-col gap-1.5">
      {canOpenFull && (
        <button
          onClick={openModal}
          className="absolute top-2 right-2 z-10 bg-white/90 rounded-full p-1 shadow-sm hover:bg-white border border-gray-100"
          aria-label="View full document"
        >
          <IconMaximize size={16} stroke={1.5} />
        </button>
      )}

      <div style={{ height: 180 }} className="w-full flex-shrink-0">
        {renderPreview()}
      </div>

      <h3 className="text-sm font-semibold text-gray-800 truncate w-full">
        {document_name}
      </h3>
      {document.name && document.name !== document_name && (
        <p className="text-[11px] text-gray-500 font-mono truncate w-full" title={document.name}>
          {document.name}
        </p>
      )}

      <div className="flex flex-row items-center gap-2">
        <DocumentationRetrievedFeedback
          documentation_retrieved_id={documentation_retrieved_id}
          feedback={feedback}
          onFeedbackSubmitted={onFeedbackSubmitted}
        />
        <span
          className={`inline-flex items-center justify-center h-7 px-2 rounded text-[10px] font-semibold tracking-wider flex-shrink-0 ${DOC_TYPE_STYLES[docType]}`}
        >
          {docType}
        </span>
        <button
          type="button"
          tabIndex={-1}
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            const scrollEl = e.currentTarget.closest('[data-docs-scroll]') as HTMLElement | null;
            const savedLeft = scrollEl?.scrollLeft ?? 0;
            setExpanded((v) => !v);
            if (scrollEl) {
              requestAnimationFrame(() => { scrollEl.scrollLeft = savedLeft; });
            }
            e.currentTarget.blur();
          }}
          aria-expanded={expanded}
          aria-label={expanded ? 'Inklappen' : 'Uitklappen'}
          className="inline-flex items-center justify-center h-7 px-2.5 rounded border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors outline-none focus:outline-none focus:ring-0 flex-shrink-0"
        >
          <IconChevronDown
            size={16}
            stroke={1.8}
            className={`transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
        </button>
      </div>

      <Modal isOpen={isModalOpen} onRequestClose={closeModal}>
        <div className="bg-white flex flex-col items-center relative w-fit h-full overflow-y-auto z-100 rounded-md">
          <div className="absolute top-2 right-2 flex gap-2 z-110">
            <button
              onClick={closeModal}
              className="p-2 rounded-full hover:bg-gray-100"
              aria-label="Close modal"
            >
              <IconX size={24} stroke={1.5} />
            </button>
          </div>

          <div className="p-16">
            <h2 className="text-xl font-bold mb-1">{document_name}</h2>
            {document.name && document.name !== document_name && (
              <p className="text-sm text-gray-500 font-mono mb-4 break-all">{document.name}</p>
            )}
            {(!document.name || document.name === document_name) && <div className="mb-4" />}
            {isTextDocument ? (
              text !== null ? (
                <pre className="whitespace-pre-wrap text-sm font-mono text-gray-800 bg-gray-50 rounded p-4 w-[80vw] max-w-3xl">
                  {text}
                </pre>
              ) : (
                <p>{textError ?? 'Tekst laden…'}</p>
              )
            ) : hasUrl ? (
              <FullPdfViewerLazy pdfUrl={document.raw_document_presigned_url!} />
            ) : (
              <p>No document available to view.</p>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Document;
